'use client';

import { useState } from 'react';
import Link from 'next/link';
import { RichText } from '@payloadcms/richtext-lexical/react';
import { LucideIcon } from '@/components/LucideIcon';
import { PayloadImage } from '@/components/PayloadImage';

function Body({ data, className, style }: { data: any; className?: string; style?: React.CSSProperties }) {
  if (!data) return null;
  if (typeof data === 'string') return <p className={className} style={style}>{data}</p>;
  return <div className={className} style={style}><RichText data={data} /></div>;
}

export function TabbedContentBlockRenderer({ block }: { block: any }) {
  const tabs = block.tabs || [];
  const [active, setActive] = useState(0);

  if (tabs.length === 0) return null;

  const isAlt = ((block._sectionIndex ?? 0) % 2) === 1;
  const imgLeft = block.imagePosition === 'left';
  const current = tabs[active] || tabs[0];

  const ctaStyle = block.ctaStyle || 'primary';
  const ctaBase = 'inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold text-sm transition-colors';
  const ctaStyles: Record<string, React.CSSProperties> = {
    primary: { backgroundColor: 'var(--color-t-cta)', color: 'var(--color-t-cta-text)' },
    outline: { border: '2px solid var(--color-t-primary)', color: 'var(--color-t-primary)', backgroundColor: 'transparent' },
    link: { color: 'var(--color-t-primary)', backgroundColor: 'transparent', textDecoration: 'underline', padding: 0 },
  };

  return (
    <section
      className="py-16 lg:py-24"
      style={{ backgroundColor: isAlt ? 'var(--color-t-section-alt)' : 'var(--color-t-section-light)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        {(block.sectionTag || block.heading || block.subheading) && (
          <div className="mb-10 lg:mb-12">
            {block.sectionTag && (
              <p
                className="text-xs sm:text-sm font-bold tracking-widest uppercase mb-3"
                style={{ color: 'var(--color-t-primary)' }}
              >
                {block.sectionTag}
              </p>
            )}
            {block.heading && (
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4"
                style={{ color: 'var(--color-t-heading)' }}
              >
                {block.heading}
              </h2>
            )}
            {block.subheading && (
              <p
                className="text-base sm:text-lg max-w-2xl"
                style={{ color: 'var(--color-t-body)' }}
              >
                {block.subheading}
              </p>
            )}
          </div>
        )}

        {/* Tab pills — desktop only (on mobile every persona is shown stacked below) */}
        <div className="hidden lg:flex flex-wrap gap-3 mb-10">
          {tabs.map((t: any, i: number) => {
            const isActive = i === active;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setActive(i)}
                className="px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
                style={
                  isActive
                    ? {
                        backgroundColor: 'var(--color-t-primary)',
                        color: 'var(--color-t-cta-text)',
                        border: '1px solid var(--color-t-primary)',
                      }
                    : {
                        backgroundColor: 'transparent',
                        color: 'var(--color-t-heading)',
                        border: '1px solid color-mix(in srgb, var(--color-t-primary) 18%, transparent)',
                      }
                }
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab content — desktop only: the active persona's content beside its visual */}
        <div data-testid="tabbed-content-desktop" className={`hidden lg:grid lg:grid-cols-2 gap-10 lg:gap-16 items-start`}>
          {/* Content column */}
          <div className={imgLeft ? 'lg:order-2' : 'lg:order-1'}>
            {current.body && (
              <Body
                data={current.body}
                className="prose prose-base max-w-none mb-6"
                style={{ color: 'var(--color-t-body)' }}
              />
            )}
            {(current.features?.length ?? 0) > 0 && (
              <ul className="flex flex-col gap-5 mt-2">
                {current.features.map((f: any, i: number) => (
                  <li key={i} className="flex items-start gap-4">
                    <span
                      className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md"
                      style={{
                        backgroundColor: 'color-mix(in srgb, var(--color-t-primary) 10%, transparent)',
                        color: 'var(--color-t-primary)',
                      }}
                    >
                      {f.iconImage?.url ? (
                        <PayloadImage media={f.iconImage} alt={f.title} targetWidth={36} className="w-5 h-5 object-contain" />
                      ) : f.icon ? (
                        <LucideIcon name={f.icon} size={18} />
                      ) : null}
                    </span>
                    <div>
                      <h4 className="text-base font-bold mb-1" style={{ color: 'var(--color-t-heading)' }}>
                        {f.title}
                      </h4>
                      <Body
                        data={f.description}
                        className="text-sm leading-relaxed"
                        style={{ color: 'var(--color-t-body)' }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Visual column */}
          <div className={imgLeft ? 'lg:order-1' : 'lg:order-2'}>
            <div
              className="rounded-2xl overflow-hidden"
              style={{
                backgroundColor: 'var(--color-t-surface)',
                border: '1px solid color-mix(in srgb, var(--color-t-primary) 10%, transparent)',
                boxShadow: '0 12px 32px color-mix(in srgb, var(--color-t-primary) 10%, transparent)',
              }}
            >
              {current.customHtml ? (
                <div className="p-6" dangerouslySetInnerHTML={{ __html: current.customHtml }} />
              ) : current.image?.url ? (
                <PayloadImage
                  media={current.image}
                  alt={current.label}
                  targetWidth={900}
                  className="w-full h-auto block"
                />
              ) : null}
            </div>
          </div>
        </div>

        {/* Mobile (< lg): switching tabs would mean tap-a-pill -> scroll-down ->
            scroll-back-up to switch. Instead stack every persona in order so you
            scroll straight through them, each clearly labelled. */}
        <div data-testid="tabbed-content-mobile" className="lg:hidden flex flex-col gap-12">
          {tabs.map((t: any, i: number) => (
            <div
              key={i}
              className={`flex flex-col gap-5 ${i > 0 ? 'pt-10 border-t' : ''}`}
              style={i > 0 ? { borderColor: 'color-mix(in srgb, var(--color-t-primary) 12%, transparent)' } : undefined}
            >
              {/* persona label — filled pill, echoes the desktop active tab */}
              <div>
                <span
                  className="inline-block px-4 py-2 rounded-full text-sm font-semibold"
                  style={{ backgroundColor: 'var(--color-t-primary)', color: 'var(--color-t-cta-text)' }}
                >
                  {t.label}
                </span>
              </div>
              {t.body && (
                <Body data={t.body} className="prose prose-base max-w-none" style={{ color: 'var(--color-t-body)' }} />
              )}
              {(t.features?.length ?? 0) > 0 && (
                <ul className="flex flex-col gap-5">
                  {t.features.map((f: any, j: number) => (
                    <li key={j} className="flex items-start gap-4">
                      <span
                        className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md"
                        style={{ backgroundColor: 'color-mix(in srgb, var(--color-t-primary) 10%, transparent)', color: 'var(--color-t-primary)' }}
                      >
                        {f.iconImage?.url ? (
                          <PayloadImage media={f.iconImage} alt={f.title} targetWidth={36} className="w-5 h-5 object-contain" />
                        ) : f.icon ? (
                          <LucideIcon name={f.icon} size={18} />
                        ) : null}
                      </span>
                      <div>
                        <h4 className="text-base font-bold mb-1" style={{ color: 'var(--color-t-heading)' }}>
                          {f.title}
                        </h4>
                        <Body data={f.description} className="text-sm leading-relaxed" style={{ color: 'var(--color-t-body)' }} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {t.customHtml ? (
                <div
                  className="rounded-2xl overflow-hidden p-6"
                  style={{ backgroundColor: 'var(--color-t-surface)', border: '1px solid color-mix(in srgb, var(--color-t-primary) 10%, transparent)' }}
                  dangerouslySetInnerHTML={{ __html: t.customHtml }}
                />
              ) : t.image?.url ? (
                <div
                  className="rounded-2xl overflow-hidden"
                  style={{
                    backgroundColor: 'var(--color-t-surface)',
                    border: '1px solid color-mix(in srgb, var(--color-t-primary) 10%, transparent)',
                    boxShadow: '0 12px 32px color-mix(in srgb, var(--color-t-primary) 10%, transparent)',
                  }}
                >
                  <PayloadImage media={t.image} alt={t.label} targetWidth={900} className="w-full h-auto block" />
                </div>
              ) : null}
            </div>
          ))}
        </div>

        {/* Shared CTA */}
        {block.ctaLabel && block.ctaLink && (
          <div className="mt-12 text-center">
            <Link href={block.ctaLink} className={ctaBase} style={ctaStyles[ctaStyle]}>
              {block.ctaLabel}
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
